import type { ReactNode } from 'react';
import { Pill } from '../ui/Pill';
import { Skeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';

export interface AssignRowItem {
  id: string;
  title: string;
  assigned: boolean;
  /**
   * Undefined means "publishing does not apply" (library tasks). False means
   * there is no published version yet, so it cannot be assigned: an inductee
   * would open it and find nothing.
   */
  published?: boolean;
  /** Optional extra chip, e.g. the task kind. */
  badge?: ReactNode;
}

/**
 * Tickable list of assignable onboarding content, used by the new-employee
 * wizard steps. Deliberately leaner than the per-person panels on the person
 * record: a brand-new starter has nothing submitted, so there is no status or
 * reopen affordance to render.
 */
export function AssignItemList({
  items,
  loading,
  emptyTitle,
  emptyDescription,
  pending,
  onToggle,
}: {
  items: AssignRowItem[] | undefined;
  loading: boolean;
  emptyTitle: string;
  emptyDescription: string;
  pending: boolean;
  onToggle: (id: string, assigned: boolean) => void;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
    );
  }

  if (!items || items.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <ul className="divide-y divide-divider/10">
      {items.map((item) => {
        // Never block un-assigning: an unpublished item may already be on the
        // person via their role template, and HR must be able to take it off.
        const locked = !item.assigned && item.published === false;
        return (
          <li key={item.id}>
            <label
              className={[
                'flex cursor-pointer items-center gap-3 px-1 py-2.5 text-sm',
                locked ? 'cursor-not-allowed opacity-60' : 'hover:bg-surface-2',
              ].join(' ')}
            >
              <input
                type="checkbox"
                checked={item.assigned}
                disabled={pending || locked}
                onChange={(e) => onToggle(item.id, e.target.checked)}
              />
              <span className="min-w-0 flex-1 truncate text-fg">{item.title}</span>
              {item.badge}
              {item.published === false ? (
                <Pill tone="warning" size="sm">
                  No published version
                </Pill>
              ) : null}
            </label>
          </li>
        );
      })}
    </ul>
  );
}
